<template>
  <AgentLayout
      :agentConfig="agentConfig"
      :statusText="statusText(agentConfig.status)"
      :isEditMode="isEditMode"
      :isProcessing="isProcessing"
      :actionButtonText="getActionButtonText()"
      :on-update="updateAgent"
      :on-weblink-share="handleWeblinkShare"
  >
    <div class="agent-page">
      <div class="agent-page-header">
        <span class="page-title">{{ $t('create_new_assistant') }}</span>
        <p>{{ $t('discover_customized_program_templates') }}</p>
      </div>
      <div class="split-view">
        <div class="split-left">
          <div class="split-header">
            <div class="left-header-holder">
              <img src="../assets/images/settings.svg" />
              <span>{{ $t('general_settings') }}</span>
            </div>
            <div class="tab-wrapper">
              <div class="tab-item" :class="{ active: activeTab === 'configure' }" @click="setActiveTab('configure')">
                <img src="../assets/images/agentTab1.svg" />
              </div>
              <div class="tab-item" :class="{ active: activeTab === 'create' }" @click="setActiveTab('create')">
                <img src="../assets/images/agentTab2.svg" />
              </div>
            </div>
          </div>
          <span class="main-divider"></span>

          <div class="builder-chat" v-if="activeTab === 'create'">
            <div v-if="!canUseBuilder" class="builder-unavailable">
              <div class="info-card">
                <span class="info-icon">💡</span>
                <div class="info-content">
                  <h4>{{ $t('builder_agent_unavailable') }}</h4>
                  <p>{{ builderUnavailableMessage }}</p>
                </div>
              </div>
            </div>

            <template v-else>
              <template v-if="currentBuilderAgent">
                <div class="messages-container" ref="builderMessagesContainer">
                  <div class="message assistant">
                    <div class="message-content">
                      <VMarkdownView
                          mode="light"
                          :content="builderInstructions"
                      ></VMarkdownView>
                    </div>
                    <div class="message-time">
                      <span>{{ formatTime(new Date()) }}</span>
                    </div>
                  </div>

                  <div
                      v-for="message in currentBuilderMessages"
                      :key="message.id"
                      class="message"
                      :class="message.role"
                  >
                    <div class="message-content">
                      <VMarkdownView
                          mode="light"
                          :content="message.message"
                      ></VMarkdownView>

                      <div class="tool-usage-container" v-if="message.run && message.run.length > 0">
                        <div class="tool-usage-header">
                          <span class="tool-icon">🔧</span>
                          <span class="tool-text">{{$t('tools_used')}}</span>
                        </div>
                        <div
                            class="tool-item"
                            v-for="(tool, index) in message.run"
                            :key="index"
                        >
                          <div class="tool-name">{{ tool.name }}</div>
                          <div class="tool-params" v-if="tool.params">
                            <pre>{{ formatToolParams(tool.params) }}</pre>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="message-time">
                      <span>{{ formatTime(message.timestamp) }}</span>
                    </div>
                  </div>

                  <div v-if="isBuilderTyping" class="message assistant typing">
                    <div class="message-content">
                      <span class="message-text">{{ $t('builder_ai_thinking') }}</span>
                    </div>
                  </div>
                </div>
                <div class="chat-container mb-2">
                  <div class="input-wrapper">
                <textarea
                    type="text"
                    class="chat-input"
                    :placeholder="this.$t('describe_what_you_change_agent')"
                    v-model="currentBuilderMessage"
                    @keypress.enter.prevent="sendBuilderMessage"
                    :disabled="isBuilderTyping"
                    rows="3"
                />
                    <img
                        v-if="!isBuilderTyping"
                        @click="sendBuilderMessage"
                        class="chat-send-button"
                        src="@/assets/images/sendArrowBlackandWhite.svg"
                    />
                    <img
                        v-if="isBuilderTyping"
                        @click="sendBuilderMessage"
                        class="chat-send-button"
                        src="@/assets/images/sendArrowGreyAndWhite.svg"
                    />

                  </div>
                </div>
              </template>
            </template>
          </div>

          <div class="builder-config" v-if="activeTab === 'configure'">
            <div v-if="isLoading" class="loading-state">
              <div class="loading-spinner"></div>
              <span>{{ isEditMode ? this.$t('loading_agent_details') +'...' : this.$t('initializing')+ '...' }}</span>
            </div>

            <div v-else-if="loadError" class="error-state">
              <span class="error-message">{{ loadError }}</span>
              <button @click="loadAgentDetails" class="retry-button">{{ $t('retry') }}</button>
            </div>

            <template v-else>
              <div class="agent-avatar-holder">
                <img
                    :src="agentProfilePicture? agentProfilePicture: agentConfig.avatarUrl"
                    class="avatar-image"
                />

                <div class="agent-avatar-right">
                  <template v-if="isEditMode">
                    <span class="avatar-image-text">{{$t('avatar_image')}}</span>
                    <span class="suggested-size">{{ $t('min_avatar_size') }}</span>
                    <div class="upload-section">
                      <input
                          type="file"
                          ref="avatarInput"
                          @change="handleAvatarUpload"
                          accept="image/*"
                          style="display: none"
                      />
                      <button @click="$refs.avatarInput.click()" class="black-white-button">
                        {{ agentConfig.avatarUrl ? this.$t('change_avatar') : this.$t('upload') }}
                      </button>

                      <div v-if="isUploadingAvatar" class="selected-files">
                        <div class="file-item">
                          <span>🔄{{$t('uploading_avatar')}}</span>
                        </div>
                      </div>


                    </div>
                  </template>
                </div>
              </div>

              <span class="divider"></span>


              <span class="form-label mt-2">{{ $t('name_person') }}</span>
              <input
                  type="text"
                  class="form-input"
                  :placeholder="this.$t('agent_name')"
                  v-model="agentConfig.name"
                  @input="onConfigChange"
              />

              <span class="form-label mt-2">{{ $t('description') }}</span>
              <textarea
                  class="form-input"
                  :placeholder="this.$t('agent_description')"
                  v-model="agentConfig.description"
                  v-textarea-autoresize
                  @input="onConfigChange"
              />

              <span class="form-label mt-2">{{ $t('about') }} ({{ agentConfig.about ? agentConfig.about.length : 0 }}/1000)</span>
              <textarea
                  class="form-input"
                  :placeholder="this.$t('about_placeholder')"
                  v-model="agentConfig.about"
                  v-textarea-autoresize
                  @input="onConfigChange"
                  maxlength="1000"
              />

              <span class="form-label mt-2">{{ $t('context') }}</span>
              <textarea
                  class="form-input"
                  :placeholder="this.$t('agent_instructions')"
                  v-model="agentConfig.context"
                  v-textarea-autoresize
                  @input="onConfigChange"
              />

              <span class="form-label mt-2">{{ $t('tags') }}</span>
              <div class="tags-container">
                <div
                    v-for="(tag, index) in tagList"
                    :key="index"
                    class="tag-item"
                    :class="{ selected: agentConfig.tagIds.includes(tag.id) }"
                    @click="toggleTag(tag.id)"
                >
                  {{ tag.name }}
                </div>
              </div>


              <span style="margin-top: 20px" class="divider"></span>

              <div class="conversation-header-holder mt-4">
                <span class="form-label">{{ $t('conversation_starters_max_5') }}</span>
                <span class="add-conversation-button"  @click="addConversationStarter(newConversationStarter)"
                >{{$t('add_conversation_starter')}}</span>
              </div>
              <textarea
                  class="form-input"
                  :placeholder="this.$t('enter_a_conversation_starter')"
                  v-model="newConversationStarter"
                  v-textarea-autoresize
                  maxlength="1000"
              />
              <div class="conversations-holder">
                <div class="conversation-item" v-for="(starter, index) in agentConfig.conversationStarters" :key="index">
                  <span>{{agentConfig.conversationStarters[index]}}</span>
                  <img src="@/assets/images/crossGrey.svg" alt="Remove" @click="removeConversationStarter(index)" />

                </div>
                <div v-if="agentConfig.conversationStarters.length >= 5" class="file-limit-warning">
                  ⚠️ {{$t('maximum_5_conversation_starters_allowed')}}
                </div>
              </div>
              <span style="margin-top: 20px" class="divider"></span>

              <div class="conversation-header-holder mt-4">
                <span class="form-label">{{ $t('qa_pairs') }}</span>
                <span class="add-conversation-button"  @click="addQA(newQuestion,newAnswer)"
                >{{$t('add_qa')}}</span>
              </div>
              <div class="new-qa-holder">
                <input
                    type="text"
                    class="form-input"
                    :placeholder="this.$t('question')"
                    v-model="newQuestion"
                    @input="onConfigChange"
                />
                <input
                    class="form-input"
                    :placeholder="$t('answer')"
                    v-model="newAnswer"
                    @input="onConfigChange"
                />
              </div>
              <span class="qa-title mt-4">{{ $t('question_answer_list') }}</span>
              <div class="qa-wrapper mt-2">
                <div class="qa-item" v-for="(qa, index) in agentConfig.qaPairs" :key="index">
                  <div class="qa-header" @click="toggleQA(index)">
                    <img
                        src="@/assets/images/arrowdown.svg"
                        class="qa-arrow"
                        :class="expandedQA===index?'rotated':''"
                    />
                    <span class="qa-question">{{ qa.question }}</span>
                    <span @click.stop="removeQA(index)">
                    <img src="@/assets/images/trashIcon.svg" alt="Remove" />
                  </span>
                  </div>
                  <div v-if="expandedQA === index" class="qa-answer-wrapper">
                    <span class="qa-answer">{{ qa.answer }}</span>
                  </div>
                </div>
              </div>
              <span style="margin-top: 20px" class="divider"></span>
              <div class="conversation-header-holder mt-4">
                <span class="form-label">{{ $t('upload_documents_max_5') }}</span>
                <span class="add-conversation-button"  @click="$refs.fileInput.click()"
                >{{$t('add_file')}}</span>
              </div>
              <div class="upload-section">
                <input
                    type="file"
                    ref="fileInput"
                    @change="handleFileUpload"
                    accept=".pdf,.doc,.docx,.ppt,.pptx,.md"
                    multiple
                    style="display: none"
                />

                <div v-if="selectedFiles.length > 0" class="selected-files">
                  <div class="files-grid">
                    <div v-for="(file, index) in selectedFiles" :key="index" class="file-card">
                      <img src="@/assets/images/pdfIcon.svg" alt="File" />
                      <span>{{ file.name }}</span>
                      <img src="@/assets/images/trashIcon.svg" @click="removeSelectedFile(index)" class="file-card-delete">
                    </div>
                  </div>
                </div>

                <div v-if="isEditMode && existingDocuments.length > 0" class="existing-documents mt-2">
                  <span class="form-label">{{ $t('existing_documents') }} ({{ existingDocuments.length }}/5):</span>
                  <div class="files-grid">
                    <div v-for="(doc, index) in existingDocuments" :key="doc.filename" class="file-card">
                      <img src="@/assets/images/pdfIcon.svg" alt="File" />
                      <span>{{ doc.originalname }}<span class="document-type">({{ doc.mimetype }})</span></span>
                      <img
                          src="@/assets/images/trashIcon.svg"
                          @click="removeExistingDocument(index, doc.filename)"
                          class="file-card-delete"
                      />
                    </div>
                  </div>
                </div>
                <div v-if="getTotalFileCount() >= 5" class="file-limit-warning">
                  ⚠️ {{$t('maximum_5_files_allowed_remove_to_add_more')}}
                </div>
              </div>

              <template v-if="isEditMode">
                <div class="delete-section mt-4">
                  <span class="form-label" style="color: #DC2626;">{{ $t('danger_zone') }}</span>
                  <div class="delete-card">
                    <div class="delete-info">
                      <h4>{{ $t('delete_agent') }}</h4>
                      <p>{{$t('delete_agent_warning')}}</p>
                    </div>
                    <button @click="showDeleteConfirmation" class="delete-button" :disabled="isDeleting">
                      {{ isDeleting ? this.$t('deleting') : this.$t('delete_agent') }}
                    </button>
                  </div>
                </div>
              </template>


            </template>
          </div>
        </div>

        <div class="split-right">
          <div v-if="!currentAgent" class="no-messages-view">
            <div class="right-split-header">
              <div class="header-logo">
                <img
                    :src="agentProfilePicture"
                    class="avatar-image"
                />
                <span>{{ agentConfig.name || this.$t('my_new_agent') }}</span>
              </div>
            </div>
            <span class="main-divider"></span>
            <div class="d-flex flex-column h-100 justify-content-between">
              <div class="mt-5">
                <img
                    :src="agentProfilePicture"
                    class="avatar-image"
                />
                <h3>{{ agentConfig.name || this.$t('my_new_agent') }}</h3>
                <span>{{ agentConfig.description || this.$t('description') }}</span>
              </div>

              <div class="mb-5">
                <div v-if="agentConfig.conversationStarters && agentConfig.conversationStarters.length > 0" class="connect-section">
                  <h3 class="form-label mt-8" >{{ $t('try_asking') }}</h3>
                  <div class="conversations-holder" style="max-width: 400px; justify-content: center">
                    <div
                        v-for="(starter, index) in agentConfig.conversationStarters.slice(0, 5)"
                        :key="index"
                        class="conversation-item"
                    >
                      "{{ starter }}"
                    </div>
                  </div>
                </div>

                <div v-if="agentToken" class="connect-section">
                  <button @click="connectToAgent" class="dark-button" :disabled="isConnectingToAgent">
                    {{ isConnectingToAgent ? this.$t('connecting_agent') : this.$t('connect_to_agent') }}
                  </button>
                </div>
                <div v-else class="connect-section">
            <span class="info-text">
              {{ isEditMode ? this.$t('agent_loaded_message'): this.$t('train_your_agent_message')}}
            </span>
                </div>
              </div>
            </div>
          </div>

          <template v-else>
            <div class="chat-header">
              <div class="agent-info">
                <img
                    :src="agentProfilePicture"
                    class="small-avatar"
                />
                <div>
                  <h4>{{ currentAgent.agentName.en || currentAgent.agentName }}</h4>
                  <span class="status">{{ isTyping ? this.$t('typing') : this.$t('online') }}</span>
                </div>
              </div>
              <button @click="disconnectFromAgent" class="dark-button-outline">{{ $t('disconnect') }}</button>
            </div>

            <div class="messages-container" ref="messagesContainer">
              <div
                  v-for="message in messages"
                  :key="message.id"
                  class="message"
                  :class="message.role"
              >
                <div class="message-content">
                  <VMarkdownView
                      mode="light"
                      :content="message.message"
                  ></VMarkdownView>
                </div>
                <div class="message-time">
                  <span>{{ formatTime(message.timestamp) }}</span>
                </div>
              </div>

              <div v-if="isTyping" class="message assistant typing">
                <div class="message-content">
                  <span class="message-text">{{ $t('agent_is_typing') }}</span>
                </div>
              </div>
            </div>
            <div class="right-chat-container mb-2">
              <div class="input-wrapper">
                <textarea
                    type="text"
                    class="chat-input"
                    :placeholder="this.$t('type_your_message_here')"
                    v-model="currentMessage"
                    @keypress.enter.prevent="sendMessage"
                    :disabled="isTyping"
                    rows="3"
                />
                <img
                    v-if="!isTyping"
                    @click="sendMessage"
                    class="chat-send-button"
                    src="@/assets/images/sendArrowBlackandWhite.svg"
                />
                <img
                    v-else
                    class="chat-send-button"
                    src="@/assets/images/sendArrowGreyAndWhite.svg"
                />

              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
    <ConfirmDeleteModal
        :isVisible="showDeleteModal"
        :isLoading="isDeleting"
        :title="this.$t('delete_agent')"
        :message="this.$t('delete_agent_warning_2')"
        :onAction="confirmDeleteAgent"
        :onClose="cancelDelete"
    />
  </AgentLayout>
</template>

<script>
import AgentLayout from "@/components/layouts/AgentLayout.vue";
import AgentSocketClient from "@/utils/agentSocket.js";
import { VMarkdownView } from 'vue3-markdown';
import agents from "@/pages/Agents.vue";
import agentService from "@/service/agentService";
import ConfirmDeleteModal from "@/components/modals/ConfirmDeleteModal.vue";
import {handleErrorResponse} from "@/utils/utils";
import tagService from "@/service/tagService";

export default {
  name: 'AgentDetails',
  components: {
    AgentLayout,
    VMarkdownView,
    ConfirmDeleteModal
  },
  data() {
    return {
      isEditMode: true,
      newAnswer: '',
      newQuestion: '',
      expandedQA: null,
      newConversationStarter: '',
      activeTab: 'configure',
      isLoading: false,
      isProcessing: false,
      isDeleting: false,
      showDeleteModal: false,
      isConnectingToAgent: false,
      isConnectingToBuilder: false,
      isUploadingAvatar: false,
      currentMessage: '',
      currentBuilderMessage: '',
      loadError: '',
      agentToken: '',
      existingDocuments: [],
      selectedFiles: [],
      hasUnsavedChanges: false,
      agentConfig: {
        name: '',
        description: '',
        avatarUrl: '',
        about: '',
        conversationStarters: [],
        context: '',
        qaPairs: [],
        status: 'draft',
        tagIds: [],
      },
      builderInstructions: '',
      tagList: [],
      tagsLoading: false,
    };
  },

  computed: {

    agentTokenFromRoute() {
      return this.isEditMode ? this.$route.params.id : '';
    },

    canUseBuilder() {
      return this.isEditMode && this.agentToken;
    },

    builderUnavailableMessage() {
      if (!this.isEditMode) {
        return this.$t('builder_save_agent_first');
      }
      if (!this.agentToken) {
        return this.$t('builder_token_not_found');
      }
      return this.$t('builder_currently_unavailable');
    },

    currentAgent() {
      return AgentSocketClient.state.currentAgent;
    },

    currentBuilderAgent() {
      return AgentSocketClient.state.builderAgent;
    },

    messages() {
      return AgentSocketClient.state.messages;
    },

    currentBuilderMessages() {
      return AgentSocketClient.state.builderMessages;
    },

    isBuilderTyping() {
      return AgentSocketClient.state.isBuilderTyping;
    },

    isTyping() {
      return AgentSocketClient.state.isTyping;
    },

    trainingStatus() {
      return AgentSocketClient.state.trainingStatus;
    },

    socketError() {
      return AgentSocketClient.state.error;
    },

    agentProfilePicture() {
      if (this.agentConfig.avatarUrl && this.agentConfig.avatarUrl !== '') {
        return this.agentConfig.avatarUrl;
      }
      return '/images/profile.svg';
    },
  },

  watch: {
    messages: {
      handler() {
        this.scrollToBottom();
      },
      deep: true
    },

    currentBuilderMessages: {
      handler(newMessages) {
        this.scrollBuilderToBottom();
        this.processBuilderToolUsage(newMessages);
      },
      deep: true
    },

    trainingStatus(newStatus) {
      if (newStatus && newStatus.success && !this.isEditMode) {
        this.agentToken = newStatus.data.token;
        this.hasUnsavedChanges = false;
      }
    },

    socketError(error) {
      if (error) {
        console.error('Socket error:', error);
      }
    }
  },

  async mounted() {
    const user = JSON.parse(localStorage.getItem('user'));

    AgentSocketClient.connect(user.userId);
    if (!this.isEditMode) {
      this.agentConfig.name = this.$t('my_new_agent');
      this.agentConfig.context = this.$t('default_agent_context');
      this.agentConfig.qaPairs = [
        {
          question: this.$t('default_qa_question_1'),
          answer: this.$t('default_qa_answer_1')
        },
        {
          question: this.$t('default_qa_question_2'),
          answer: this.$t('default_qa_answer_2')
        }
      ];
    }

    this.builderInstructions =
        "# 🤖" + this.$t('builder_instructions_1') + "\n" +
        "\n" + this.$t('builder_instructions_2') +
        "\n\n" +
        "## " + this.$t('builder_instructions_3') + "\n" +
        "- **" + this.$t('builder_instructions_4') + "\n" +
        "- **" + this.$t('builder_instructions_5') + "\n" +
        "- **" + this.$t('builder_instructions_6') + "\n" +
        "\n" +
        "## " + this.$t('builder_instructions_7') + "\n" +
        "- " + this.$t('builder_instructions_8') + "\n" +
        "- " + this.$t('builder_instructions_9') + "\n" +
        "- " + this.$t('builder_instructions_10') + "\n" +
        "- " + this.$t('builder_instructions_11') + "\n" +
        "- " + this.$t('builder_instructions_12') + "\n" +
        "- " + this.$t('builder_instructions_13') + "\n" +
        "- " + this.$t('builder_instructions_14') + "\n" +
        "\n" +
        this.$t('builder_instructions_15');

    if (this.isEditMode) {
      await this.loadAgentDetails();
    }

    this.loadTags();

    window.addEventListener('beforeunload', this.handleBeforeUnload);
  },

  beforeUnmount() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    AgentSocketClient.disconnect();
  },

  methods: {
    loadTags() {
      this.tagsLoading = true;
      tagService.getDefaultTags().then((response) => {

          console.log('Default tags loaded:', response.data);
          this.tagList = response.data.data.tag;

      }).catch((error) => {
        console.error('Error loading tags:', error);
      }).finally(() => {
        this.tagsLoading = false;
      });
    },
    toggleTag(tagId) {
      const index = this.agentConfig.tagIds.indexOf(tagId);
      if (index === -1) {
        this.agentConfig.tagIds.push(tagId);
      } else {
        this.agentConfig.tagIds.splice(index, 1);
      }
      this.onConfigChange();
    },
    handleWeblinkShare() {
      const weblink = `https://web.ibilityapp.com/agent/${this.agentToken}`;

      navigator.clipboard.writeText(weblink).then(() => {
        this.$snackbar.add({
          type: 'success',
          text: this.$t('weblink_copied_to_clipboard')
        });
      });
    },

    statusText(statusKey) {
      const statusMap = {
        'draft': this.$t('ready'),
        'pending': this.$t('pending'),
        'training': this.$t('training'),
        'published': this.$t('published'),
        'failed': this.$t('failed'),
        'deleted': this.$t('deleted')
      };

      const status = statusKey || 'draft';
      return statusMap[status] || status.charAt(0).toUpperCase() + status.slice(1);
    },

    toggleQA(index) {
      this.expandedQA = this.expandedQA === index ? null : index;
    },

    setActiveTab(tab) {
      if (tab === 'create' && !this.canUseBuilder) {
        return;
      }

      if (!this.currentBuilderAgent && !this.isConnectingToBuilder) {
        this.connectToBuilderAgent();
      }

      this.activeTab = tab;
    },

    async loadAgentDetails() {
      if (!this.isEditMode) return;

      try {
        this.isLoading = true;
        this.loadError = '';

        const response = await agentService.getAgentById(this.agentTokenFromRoute);

        const result = response.data;

        if (result.success) {
          const agent = result.agent;
          this.updateAgentConfig(agent);
          this.agentToken = agent.token;
          this.existingDocuments = agent.metadata?.uploadedFiles || [];
          this.hasUnsavedChanges = false;
        } else {
          this.loadError = result.error || 'Failed to load agent details';
        }
      } catch (error) {
        console.error('Error loading agent:', error);
        handleErrorResponse(error, this.$snackbar);
        this.loadError = 'Network error while loading agent details';
      } finally {
        this.isLoading = false;
      }
    },

    updateAgentConfig(agent) {
      this.agentConfig.name = agent.name?.en || agent.name || '';
      this.agentConfig.description = agent.description || '';
      this.agentConfig.avatarUrl = agent.avatarUrl || '';
      this.agentConfig.about = agent.about || '';
      this.agentConfig.conversationStarters = agent.conversationStarters || [];
      this.agentConfig.context = agent.metadata?.context || '';
      this.agentConfig.qaPairs = agent.metadata?.qaPairs || [];
      this.agentConfig.status = agent.status || 'draft';
      this.agentConfig.tagIds = agent.tagIds || [];
    },

    onConfigChange() {
      this.hasUnsavedChanges = true;
    },

    async handleAvatarUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      if (!file.type.startsWith('image/')) {
        this.$snackbar.add({
          type: 'warning',
          text: this.$t('please_select_an_image_file')
        });
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        this.$snackbar.add({
          type: 'warning',
          text: this.$t('image_file_must_be_less_than_5_mb')
        });
        return;
      }

      try {
        this.isUploadingAvatar = true;

        const formData = new FormData();
        formData.append('avatar', file);

        agentService.uploadAgentAvatar(formData).then((response) => {
          if (response.data.success) {
            this.agentConfig.avatarUrl = response.data.avatarUrl;
            this.onConfigChange();

            this.$snackbar.add({
              type: 'success',
              text: this.$t('avatar_uploaded_successfully')
            });

          } else {

            this.$snackbar.add({
              type: 'error',
              text: this.$t('failed_to_upload_avatar') + ': ' + response.data.error
            });
          }
        });
      } catch (error) {
        console.error('Avatar upload error:', error);

        this.$snackbar.add({
          type: 'error',
          text: this.$t('network_error_while_uploading_avatar')
        });
      } finally {
        this.isUploadingAvatar = false;
        event.target.value = '';
      }
    },

    removeAvatar() {
      if (confirm(this.$t('avatar_delete_warning'))) {
        this.agentConfig.avatarUrl = '';
        this.onConfigChange();
      }
    },

    resetAvatarUrl(event) {
      event.target.style.display = 'none';
    },

    addConversationStarter(conversation) {
      if (this.agentConfig.conversationStarters.length < 5) {
        this.agentConfig.conversationStarters.push(conversation);
        this.newConversationStarter = '';
        this.onConfigChange();
      }
    },

    removeConversationStarter(index) {
      this.agentConfig.conversationStarters.splice(index, 1);
      this.onConfigChange();
    },

    addQA(question, answer) {
      this.agentConfig.qaPairs.push({ question: question, answer: answer });
      this.newQuestion = '';
      this.newAnswer = '';
      this.onConfigChange();
    },

    removeQA(index) {
      this.agentConfig.qaPairs.splice(index, 1);
      this.onConfigChange();
    },

    handleFileUpload(event) {
      const files = Array.from(event.target.files);
      const totalFiles = this.getTotalFileCount() + files.length;

      if (totalFiles > 5) {
        this.$snackbar.add({
          type: 'warning',
          text: this.$t('cannot_add_files_max_5')
        });
        return;
      }

      this.selectedFiles = [...this.selectedFiles, ...files];
      this.onConfigChange();
      event.target.value = '';
    },

    removeSelectedFile(index) {
      this.selectedFiles.splice(index, 1);
      this.onConfigChange();
    },

    async removeExistingDocument(index, fileId) {
      if (!confirm(this.$t('delete_file_warning'))) {
        return;
      }

      try {
        if (this.isEditMode) {
          const response = await agentService.removeDocument(this.$route.params.id, fileId).catch((error) => {
            console.error('Error removing document:', error);
            throw error;
          })
        }

        this.existingDocuments.splice(index, 1);
        this.onConfigChange();

        this.$snackbar.add({
          type: 'success',
          text: this.$t('file_deleted_successfully')
        });

      } catch (error) {
        console.error('Error deleting file:', error);

        this.$snackbar.add({
          type: 'error',
          text: this.$t('failed_to_delete_file')
        });
      }
    },

    getTotalFileCount() {
      return this.selectedFiles.length + this.existingDocuments.length;
    },

    async updateAgent() {
      try {
        this.isProcessing = true;
        AgentSocketClient.clearTrainingStatus();

        const formData = this.createFormData();
        const response = await agentService.updateAgent(this.agentTokenFromRoute, formData);

        const result = response.data;

        if (result.success) {
          this.selectedFiles = [];
          this.hasUnsavedChanges = false;

          this.$snackbar.add({
            type: 'success',
            text: this.$t('agent_updated_successfully')
          });

          await this.loadAgentDetails();
        } else {
          console.error('Update failed:', result.error);
          handleErrorResponse(result, this.$snackbar);
        }
      } catch (error) {
        console.error('Update error:', error);
        handleErrorResponse(error, this.$snackbar);
      } finally {
        this.isProcessing = false;
      }
    },

    createFormData() {
      const user = JSON.parse(localStorage.getItem('user'));

      const formData = new FormData();
      formData.append('name', this.agentConfig.name);
      formData.append('description', this.agentConfig.description);
      formData.append('avatarUrl', this.agentConfig.avatarUrl || '');
      formData.append('about', this.agentConfig.about || '');
      formData.append('conversationStarters', JSON.stringify(
          this.agentConfig.conversationStarters.filter(starter => starter.trim())
      ));
      formData.append('context', this.agentConfig.context);
      formData.append('userId', user.userId);
      formData.append('companyId', user.companyId);
      formData.append('qaPairs', JSON.stringify(
          this.agentConfig.qaPairs.filter(qa => qa.question.trim() && qa.answer.trim())
      ));
      formData.append('tagIds', JSON.stringify(this.agentConfig.tagIds));

      this.selectedFiles.forEach((file) => {
        formData.append('documents', file);
      });

      return formData;
    },

    showDeleteConfirmation() {
      this.showDeleteModal = true;
    },

    cancelDelete() {
      this.showDeleteModal = false;
    },

    async confirmDeleteAgent() {
      try {
        const user = JSON.parse(localStorage.getItem('user'));

        this.isDeleting = true;

        const response = await agentService.deleteAgent(this.agentTokenFromRoute, user.userId);

        const result = response.data;

        if (result.success) {
          this.showDeleteModal = false;

          this.$snackbar.add({
            type: 'success',
            text: this.$t('agent_deleted_successfully')
          });

          window.location.href = '/agents';
        } else {
          console.error('Delete failed:', result.error);

          this.$snackbar.add({
            type: 'error',
            text: this.$t('failed_to_delete_agent') + ': ' + result.error
          });
        }
      } catch (error) {
        console.error('Delete error:', error);

        this.$snackbar.add({
          type: 'error',
          text: this.$t('network_error_while_deleting_agent')
        });
      } finally {
        this.isDeleting = false;
      }
    },

    async connectToAgent() {
      if (this.agentToken) {
        try {
          this.isConnectingToAgent = true;
          await AgentSocketClient.joinAgent(this.agentToken);
        } catch (error) {
          console.error('Failed to connect to agent:', error);
        } finally {
          this.isConnectingToAgent = false;
        }
      }
    },

    async connectToBuilderAgent() {
      if (!this.canUseBuilder) {
        console.warn('Builder agent not available');
        return;
      }

      try {
        this.isConnectingToBuilder = true;
        await AgentSocketClient.joinAgentBuilder(this.agentToken);
      } catch (error) {
        console.error('Failed to connect to builder agent:', error);
      } finally {
        this.isConnectingToBuilder = false;
      }
    },

    disconnectFromAgent() {
      AgentSocketClient.leaveAgent();
    },

    async sendMessage() {
      if (this.currentMessage.trim() && this.currentAgent) {
        try {
          await AgentSocketClient.sendMessage(this.currentMessage.trim());
          this.currentMessage = '';
        } catch (error) {
          console.error('Failed to send message:', error);
        }
      }
    },

    async sendBuilderMessage() {
      if (this.currentBuilderMessage.trim() && this.currentBuilderAgent) {
        try {
          await AgentSocketClient.sendBuilderMessage(this.currentBuilderMessage.trim());
          this.currentBuilderMessage = '';
        } catch (error) {
          console.error('Failed to send builder message:', error);
        }
      }
    },

    processBuilderToolUsage(messages) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage && lastMessage.role === 'assistant' && lastMessage.run) {
        lastMessage.run.forEach(tool => {
          this.handleBuilderTool(tool);
        });
      }
    },

    handleBuilderTool(tool) {
      switch (tool.key) {
        case 'edit_qa':
          this.handleEditQA(tool.params);
          break;
        case 'add_qa':
          this.handleAddQA(tool.params);
          break;
        case 'remove_qa':
          this.handleRemoveQA(tool.params);
          break;
        case 'edit_context':
          this.handleEditContext(tool.params);
          break;
        case 'edit_avatar':
          this.handleEditAvatar(tool.params);
          break;
        case 'edit_about':
          this.handleEditAbout(tool.params);
          break;
        case 'add_conversation_starter':
          this.handleAddConversationStarter(tool.params);
          break;
        case 'remove_conversation_starter':
          this.handleRemoveConversationStarter(tool.params);
          break;
        case 'edit_name':
          this.handleEditName(tool.params);
          break;
        case 'edit_description':
          this.handleEditDescription(tool.params);
          break;
        case 'train_agent':
          this.handleTrainAgent();
          break;
        default:
          console.warn('Unknown tool:', tool.key);
      }
    },

    handleEditQA(params) {
      if (params && typeof params.index !== 'undefined' && this.agentConfig.qaPairs[params.index]) {
        if (params.question) {
          this.agentConfig.qaPairs[params.index].question = params.question;
        }
        if (params.answer) {
          this.agentConfig.qaPairs[params.index].answer = params.answer;
        }
        this.onConfigChange();
      }
    },

    handleAddQA(params) {
      if (params && params.question && params.answer) {
        this.agentConfig.qaPairs.push({
          question: params.question,
          answer: params.answer
        });
        this.onConfigChange();
      }
    },

    handleRemoveQA(params) {
      if (params && typeof params.index !== 'undefined' && this.agentConfig.qaPairs[params.index]) {
        this.agentConfig.qaPairs.splice(params.index, 1);
        this.onConfigChange();
      }
    },

    handleEditContext(params) {
      if (params && params.context) {
        this.agentConfig.context = params.context;
        this.onConfigChange();
      }
    },

    handleEditAvatar(params) {
      if (params && params.avatarUrl !== undefined) {
        this.agentConfig.avatarUrl = params.avatarUrl;
        this.onConfigChange();
      }
    },

    handleEditAbout(params) {
      if (params && params.about !== undefined) {
        this.agentConfig.about = params.about;
        this.onConfigChange();
      }
    },

    handleAddConversationStarter(params) {
      if (params && params.starter && this.agentConfig.conversationStarters.length < 5) {
        this.agentConfig.conversationStarters.push(params.starter);
        this.onConfigChange();
      }
    },

    handleRemoveConversationStarter(params) {
      if (params && typeof params.index !== 'undefined' && this.agentConfig.conversationStarters[params.index]) {
        this.agentConfig.conversationStarters.splice(params.index, 1);
        this.onConfigChange();
      }
    },

    handleEditName(params) {
      if (params && params.name) {
        this.agentConfig.name = params.name;
        this.onConfigChange();
      }
    },

    handleEditDescription(params) {
      if (params && params.description !== undefined) {
        this.agentConfig.description = params.description;
        this.onConfigChange();
      }
    },

    async handleTrainAgent() {
        await this.updateAgent();
    },

    formatTime(timestamp) {
      return new Date(timestamp).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    },

    formatToolParams(params) {
      if (typeof params === 'string') {
        return params;
      }
      return JSON.stringify(params, null, 2);
    },

    getActionButtonText() {
      if (this.isProcessing) {
        return this.isEditMode ? this.$t('updating') + "..." : this.$t("training") + '...';
      }
      return this.isEditMode ? this.$t('update_agent') : this.$t('train_agent');
    },

    scrollToBottom() {
      this.$nextTick(() => {
        if (this.$refs.messagesContainer) {
          this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer.scrollHeight;
        }
      });
    },

    scrollBuilderToBottom() {
      this.$nextTick(() => {
        if (this.$refs.builderMessagesContainer) {
          this.$refs.builderMessagesContainer.scrollTop = this.$refs.builderMessagesContainer.scrollHeight;
        }
      });
    },

    handleBeforeUnload(event) {
      if (this.hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = '';
      }
    }
  },
}
</script>

<style scoped>
.header-logo{
  display: flex;
  align-items: center !important;
  gap: 8px;
}
.header-logo img{
  height: 40px;
  width: 40px;
}
.header-logo span{
  font-family: euclid_medium,sans-serif !important;
  font-size: 20px !important;
  color: #101828 !important;
  margin-bottom: 0px!important;
}
.right-split-header{
  padding: 10px;
  display: flex;
  align-self: flex-start;
}


.chat-send-button{
  align-self:flex-end;
}
.chat-input {
  display: flex;
  width: 100%;
  border: none;
  align-self: flex-start;
  min-height: 72px;
  max-height: 72px;
  resize: none;
  padding: 12px;
  font-family: "Euclid Circular A", sans-serif;
  font-size: 14px;
  text-decoration-color: #667085;
  line-height: 20px;
  outline: none;
  background-color: transparent;
}

.files-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
}

.file-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 16px 16px 16px;
  border: 1px solid #EAECF0;
  border-radius: 12px;
  font-family: euclid_medium, sans-serif;
  font-size: 12px;
  color: #101828;
  position: relative;
  gap: 12px;
  min-width: 30%;
  max-width: calc(33.333% - 6px);
  flex: 1;
}

.file-card img:first-child {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
}

.file-card span {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
  width: 100%;
}

.file-card-delete {
  position: absolute;
  top: 10px;
  right: 12px;
  width: 14px;
  height: 14px;
  cursor: pointer;
}
.qa-title{
  font-family: 'euclid_medium', sans-serif;
  font-size: 12px;
  color: #667085;
}
.qa-item {
  flex-direction: column;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  padding: 0;
  margin-bottom: 8px;
}

.qa-header {
  display: flex;
  align-items: center;
  padding: 12px;
  cursor: pointer;
  gap: 12px;
}

.qa-arrow {
  width: 16px;
  height: 16px;
  transform: rotate(-90deg);

}
.qa-arrow.rotated{
  transform: rotate(0deg);
}
.qa-question {
  flex: 1;
  font-family: euclid_medium, sans-serif;
  font-size: 14px;
  color: #101828;
}

.qa-answer-wrapper {
  padding: 0 12px 12px 40px;
}

.qa-answer {
  font-family: euclid_regular, sans-serif;
  font-size: 14px;
  color: #475467;
}
.new-qa-holder{
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap:8px
}
.conversation-item{
  display: flex;
  flex-direction: row;
  border: 1px solid #D0D5DD;
  border-radius: 3000px;
  padding: 4px 12px 4px 12px;
  gap: 8px;
  align-items: center;
  justify-content: center;
}
.conversation-item span{
  font-family: 'euclid_medium', sans-serif;
  font-size: 12px;
  color: #475467;
}
.conversation-item img{
  width: 10px;
  height: 10px;
}
.conversations-holder{
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}

.add-conversation-button{
  align-self: flex-start;
  font-size: 12px;
  color: #4A68F8;
  cursor: pointer;

}
.conversation-header-holder{
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 5px;
}
.main-divider {
  background-color:#e2e4e9 ;
  height: 1px !important;
  width: 90%;
  align-self: center;
  padding: 0 24px;
}
.divider {
  background-color:#e2e4e9 ;
  min-height: 1px !important;
  width: 100%;
  align-self: center;
  padding: 0 24px;
}
.avatar-image-text{
  font-family: euclid_medium, sans-serif;
  font-size: 16px;
  color: #101828;
}
.black-white-button{
  background-color: transparent;
  border: 1px solid #98A2B3;
  color: #040C2D;
  padding: 5px 20px 5px 20px;
  border-radius: 12px;
  width: fit-content;
  max-width: 100%;
  font-size: 14px;
  margin-top: 2px;
}
.agent-avatar-right{
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 2px;
}
.suggested-size{
  font-family: euclid_regular, sans-serif;
  font-size: 14px;
  color: #667085;
}
.agent-avatar-holder{
  display: flex;
  flex-direction: row;
  align-items: center;
}
.left-header-holder{
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}
.left-header-holder span{
  font-family: euclid_medium, sans-serif;
  font-size: 20px;
  color: #101828;
}
.left-header-holder img{
  width: 24px;
  height: 24px;
}
.agent-page-header{
  margin-bottom: 24px;

}

.agent-page-header p{
  color: #475467;
  font-size: 14px;
}
.agent-page-header span{
  color: #101828;
  font-size: 20px;
}
.agent-page{
  background-color: var(--vt-c-white-soft);
  padding: 40px;
}
.split-view {
  display: flex;
  height: calc(100vh - 64px);
  width: 100%;
  gap: 20px;
}

.split-left, .split-right {
  box-shadow: 0 0 0 1px #EAECF0;
  border-radius: 12px;
  padding: 5px;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 64px);
}

.split-left {
  flex: 0.4;
  background-color: #fff;
  border-right: 1px solid #EAECF0;
  padding-top: 20px;
  height: 100%;
}

.split-right {
  flex: 0.6;

  background-color: #FCFCFD;
}

/* Header and Tabs */
.split-header {
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 20px;
  justify-content: space-between;
  padding: 0 24px;
}

.tab-wrapper {
  display: flex;
  justify-content: space-around;
  background-color: #F6F8FA;
  border-radius: 14px;
  padding: 5px 10px;
  gap: 6px;
}

.tab-item {
  padding: 2px 15px;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.tab-item.active {
  background-color: #FFFFFF;
  box-shadow:-4px 6px 10px rgba(16,24,32,0.06);


}

.tab-item:hover:not(.active) {
  background-color: #f8f9fa;
}

/* Builder Chat Styles */
.builder-chat {
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  flex: 1;
  position: relative;
  padding-bottom: 80px;
}

.builder-unavailable {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 40px 20px;
}

.info-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 20px;
  background-color: #FEF3C7;
  border: 1px solid #F59E0B;
  border-radius: 12px;
  max-width: 400px;
}

.info-icon {
  font-size: 24px;
  flex-shrink: 0;
}

.info-content h4 {
  margin: 0 0 8px 0;
  font-family: euclid_medium, sans-serif;
  font-size: 16px;
  color: #92400E;
}

.info-content p {
  margin: 0;
  font-family: euclid_regular, sans-serif;
  font-size: 14px;
  color: #92400E;
  line-height: 1.4;
}

/* Messages Container */
.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  scroll-behavior: smooth;
  -ms-overflow-style: none;     /* IE / eski Edge */
  scrollbar-width: none;
}

.chat-container {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  margin: 0px 20px 0px 20px;
  border: 1px solid #E5E7EB;
  border-radius: 20px;
  background-color: white;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  z-index: 10;
}
.right-chat-container{
  position: relative;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  margin: 0px 20px 0px 20px;
  border: 1px solid #E5E7EB;
  border-radius: 20px;
  background-color: white;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  z-index: 10;
}
.message {
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
}

.message.received, .message.assistant {
  align-items: flex-start;
}

.message.sent, .message.user {
  align-items: flex-end;
}

.message-content {
  max-width: 100%;
  padding: 12px 16px;
  border-radius: 12px;
  font-family: euclid_regular, sans-serif;
  font-size: 14px;
  line-height: 1.4;
}

.message.received .message-content,
.message.assistant .message-content {
  background-color: #F1F4F8;
  color: #040C2D;
}

.message.sent .message-content,
.message.user .message-content {
  background-color: #E0F2FE;
  color: #040C2D;
}

.message.typing .message-content {
  background-color: #f0f0f0;
  font-style: italic;
  color: #666;
}

.message-time {
  font-family: euclid_regular, sans-serif;
  font-size: 11px;
  color: #888888;
  margin-top: 4px;
  padding: 0 4px;
}

/* Tool Usage Display */
.tool-usage-container {
  margin-top: 12px;
  padding: 12px;
  background-color: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
}

.tool-usage-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}

.tool-icon {
  font-size: 14px;
}

.tool-text {
  font-family: euclid_medium, sans-serif;
  font-size: 12px;
  color: #475569;
}

.tool-item {
  margin-bottom: 8px;
  padding: 8px;
  background-color: white;
  border-radius: 6px;
  border: 1px solid #E2E8F0;
}

.tool-name {
  font-family: euclid_medium, sans-serif;
  font-size: 13px;
  color: #3B82F6;
  margin-bottom: 4px;
}

.tool-params {
  font-family: 'Courier New', monospace;
  font-size: 11px;
  color: #64748B;
  background-color: #F1F5F9;
  padding: 6px 8px;
  border-radius: 4px;
  overflow-x: auto;
}

.tool-params pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}

/* Builder Instructions */
.builder-instructions {
  margin: 16px 0;
  padding: 16px;
  background-color: #F0F9FF;
  border: 1px solid #BAE6FD;
  border-radius: 8px;
}

.instructions-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.instructions-icon {
  font-size: 16px;
}

.instructions-header span {
  font-family: euclid_medium, sans-serif;
  font-size: 14px;
  color: #0F172A;
}

.builder-instructions p {
  margin: 0 0 8px 0;
  font-family: euclid_regular, sans-serif;
  font-size: 13px;
  color: #475569;
}

.builder-instructions ul {
  margin: 0;
  padding-left: 20px;
}

.builder-instructions li {
  font-family: euclid_regular, sans-serif;
  font-size: 13px;
  color: #64748B;
  margin-bottom: 4px;
}

/* Input and Buttons */
.input-wrapper {
  padding: 16px 20px;
  display: flex;
  gap: 12px;
  width: 100%;
}


.send-button {
  padding: 12px 20px;
  background-color: #3B82F6;
  color: #FFFFFF;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-family: euclid_medium, sans-serif;
  font-size: 14px;
  white-space: nowrap;
  transition: background-color 0.2s ease;
}

.send-button:hover:not(:disabled) {
  background-color: #2563EB;
}

.send-button:disabled {
  background-color: #D1D5DB;
  cursor: not-allowed;
}

/* Configuration Form */
.builder-config {
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
  overflow-y: auto;
  flex: 1;
}

.loading-state, .error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
  flex: 1;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f4f6;
  border-top: 4px solid #3B82F6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-message {
  color: #DC2626;
  font-family: euclid_medium, sans-serif;
  margin-bottom: 16px;
  font-size: 14px;
}

.retry-button {
  padding: 8px 16px;
  background-color: #3B82F6;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-family: euclid_medium, sans-serif;
  font-size: 14px;
}

.avatar-image {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  align-self: center;
}

.small-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
}

.edit-mode-badge {
  background-color: #FEF3C7;
  color: #92400E;
  padding: 8px 16px;
  border-radius: 9px;
  font-family: euclid_medium, sans-serif;
  font-size: 12px;
  border: 1px solid #FBBF24;
  align-self: center;
}

.form-label {
  margin-bottom: 4px;
  align-self: flex-start;
  font-family: euclid_medium, sans-serif;
  font-size: 16px;
  color: #101828;
}

/* File Upload Section */
.upload-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.upload-button {
  padding: 10px 16px;
  background-color: #6B7280;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-family: euclid_medium, sans-serif;
  font-size: 14px;
  align-self: flex-start;
  transition: background-color 0.2s ease;
}

.upload-button:hover {
  background-color: #4B5563;
}

.selected-files, .existing-documents {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.file-item{
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background-color: #F9FAFB;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  margin-bottom: 8px;
  font-family: euclid_regular, sans-serif;
  font-size: 14px;
}

.document-item{
  display: flex;
  position: relative;
  flex-direction: column;
  align-items: center;
  padding: 16px;
  border: 1px solid #EAECF0;
  border-radius: 12px;
  font-family: euclid_medium, sans-serif;
  font-size: 12px;
  text-decoration-color: #101828;
}

.document-type {
  color: #6B7280;
  font-size: 12px;
  margin-left: 8px;
}

.remove-file-button {
  background-color: #EF4444;
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  line-height: 1;
  transition: background-color 0.2s ease;
}

.remove-file-button:hover {
  background-color: #DC2626;
}

.file-limit-warning {
  color: #D97706;
  font-family: euclid_medium, sans-serif;
  font-size: 14px;
  padding: 10px 12px;
  background-color: #FEF3C7;
  border: 1px solid #FBBF24;
  border-radius: 6px;
}

/* Q&A Section */
.qa-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 12px;
}


.qa-item .form-input {
  flex: 1;
}

.remove-button {
  padding: 8px 12px;
  background-color: #EF4444;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-family: euclid_medium, sans-serif;
  font-size: 16px;
  min-width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
}

.remove-button:hover {
  background-color: #DC2626;
}

.add-button {
  padding: 10px 16px;
  background-color: #10B981;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-family: euclid_medium, sans-serif;
  font-size: 14px;
  align-self: flex-start;
  transition: background-color 0.2s ease;
}

.add-button:hover {
  background-color: #059669;
}

/* Status Messages */
.training-status {
  padding: 12px 16px;
  border-radius: 8px;
  font-family: euclid_medium, sans-serif;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
}

.training-status.success {
  background-color: #D1FAE5;
  color: #065F46;
  border: 1px solid #A7F3D0;
}

.training-status.error {
  background-color: #FEE2E2;
  color: #991B1B;
  border: 1px solid #FCA5A5;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 12px;
  align-items: center;
  width: 100%;
}

.train-button {
  padding: 12px 24px;
  background-color: #3B82F6;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-family: euclid_medium, sans-serif;
  font-size: 14px;
  transition: background-color 0.2s ease;
}

.train-button:hover:not(:disabled) {
  background-color: #2563EB;
}

.train-button:disabled {
  background-color: #9CA3AF;
  cursor: not-allowed;
}

.delete-button {
  padding: 12px 24px;
  background-color: #DC2626;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-family: euclid_medium, sans-serif;
  font-size: 14px;
  transition: background-color 0.2s ease;
}

.delete-button:hover:not(:disabled) {
  background-color: #B91C1C;
}

.delete-button:disabled {
  background-color: #9CA3AF;
  cursor: not-allowed;
}

/* Right Panel Styles */
.no-messages-view {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  padding: 20px 20px;
  text-align: center;
}

.no-messages-view h3 {
  font-family: euclid_medium, sans-serif;
  font-size: 20px;
  color: #040C2D;
  margin: 0 0 8px 0;
}

.no-messages-view span {
  font-family: euclid_regular, sans-serif;
  font-size: 14px;
  color: #667085;
  margin-bottom: 24px;
}

.connect-section {
  margin-top: 20px;
}

.connect-button {
  padding: 12px 24px;
  background-color: #3B82F6;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-family: euclid_medium, sans-serif;
  font-size: 14px;
  transition: background-color 0.2s ease;
}

.connect-button:hover:not(:disabled) {
  background-color: #2563EB;
}

.connect-button:disabled {
  background-color: #9CA3AF;
  cursor: not-allowed;
}

.info-text {
  font-family: euclid_regular, sans-serif;
  font-size: 14px;
  color: #667085;
  text-align: center;
}

/* Chat Header */
.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #EAECF0;
  background-color: white;
}

.agent-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.agent-info h4 {
  margin: 0;
  font-family: euclid_medium, sans-serif;
  font-size: 16px;
  color: #040C2D;
}

.status {
  font-family: euclid_regular, sans-serif;
  font-size: 12px;
  color: #667085;
}

.disconnect-button {
  padding: 8px 16px;
  background-color: #F87171;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-family: euclid_medium, sans-serif;
  font-size: 12px;
  transition: background-color 0.2s ease;
}

.disconnect-button:hover {
  background-color: #EF4444;
}

/* Delete Section Styles */
.delete-section {
  width: 100%;
  margin-top: 20px;
}

.delete-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background-color: #FEF2F2;
  border: 1px solid #FECACA;
  border-radius: 12px;
  gap: 20px;
}

.delete-info {
  flex: 1;
}

.delete-info h4 {
  margin: 0 0 4px 0;
  font-family: euclid_medium, sans-serif;
  font-size: 16px;
  color: #991B1B;
}

.delete-info p {
  margin: 0;
  font-family: euclid_regular, sans-serif;
  font-size: 14px;
  color: #7F1D1D;
  line-height: 1.4;
}

.delete-button {
  padding: 10px 20px;
  background-color: #DC2626;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-family: euclid_medium, sans-serif;
  font-size: 14px;
  white-space: nowrap;
  transition: background-color 0.2s ease;
}

.delete-button:hover:not(:disabled) {
  background-color: #B91C1C;
}

.delete-button:disabled {
  background-color: #9CA3AF;
  cursor: not-allowed;
}

/* Responsive Design */
@media (max-width: 768px) {
  .split-view {
    flex-direction: column;
    height: auto;
  }

  .split-left, .split-right {
    width: 100%;
    max-height: none;
  }

  .qa-item {
    flex-direction: column;
  }

  .action-buttons {
    flex-direction: column;
  }

  .tab-wrapper {
    width: 90%;
  }
}

.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.tag-item {
  padding: 6px 16px;
  border-radius: 3000px;
  border: 1px solid #D0D5DD;
  font-family: euclid_medium, sans-serif;
  font-size: 13px;
  color: #344054;
  cursor: pointer;
  transition: all 0.15s ease;
  user-select: none;
}

.tag-item:hover {
  border-color: #4A68F8;
  color: #4A68F8;
}

.tag-item.selected {
  background-color: #4A68F8;
  border-color: #4A68F8;
  color: #FFFFFF;
}
</style>
